<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ForwardingApi from 'dashboard/api/messageForwarding';
import { FORWARD_CHANNELS } from 'dashboard/helper/messageForwarding';

const props = defineProps({
  sourceId: { type: Number, required: true },
  messageIds: { type: Array, required: true },
});
const emit = defineEmits(['back', 'close']);
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
const searchInput = ref(null);
const query = ref('');
const conversations = ref([]);
const selected = ref([]);
const page = ref(1);
const hasMore = ref(false);
const loading = ref(false);
const busy = ref(false);
const attempted = ref(false);
const results = ref([]);
const error = ref('');
const statusFailed = ref(false);
const pendingCount = computed(
  () => results.value.filter(result => result.status === 'scheduled').length
);
const viewportHeight = ref(window.visualViewport?.height ?? window.innerHeight);
const viewportTop = ref(window.visualViewport?.offsetTop ?? 0);
let generation = 0;
let timer;
let statusTimer;
let active = true;
let previousFocus;
function updateViewport() {
  viewportHeight.value = window.visualViewport?.height ?? window.innerHeight;
  viewportTop.value = window.visualViewport?.offsetTop ?? 0;
}
const requestId = crypto.randomUUID();

const inboxFor = conversation =>
  conversation.inbox ||
  store.getters['inboxes/getInbox'](conversation.inbox_id) ||
  {};
const selectable = conversation =>
  FORWARD_CHANNELS.includes(inboxFor(conversation).channel_type) &&
  conversation.can_reply !== false;
const recipient = conversation =>
  conversation.meta?.sender || conversation.contact || {};
const total = computed(() => props.messageIds.length * selected.value.length);
const canSend = computed(
  () => selected.value.length && !busy.value && !attempted.value
);

async function refreshStatus() {
  clearTimeout(statusTimer);
  statusFailed.value = false;
  error.value = '';
  try {
    const response = await ForwardingApi.status({
      sourceId: props.sourceId,
      requestId,
    });
    if (!active) return;
    if (!Array.isArray(response.data?.results))
      throw new Error('Missing forwarding status');
    results.value = response.data.results;
    if (pendingCount.value) statusTimer = setTimeout(refreshStatus, 3000);
  } catch {
    if (!active) return;
    statusFailed.value = true;
    error.value = t('CONVERSATION.FORWARD.STATUS_ERROR');
  }
}

async function fetchConversations(append = false) {
  generation += 1;
  const token = generation;
  loading.value = true;
  error.value = '';
  if (!append) {
    page.value = 1;
    conversations.value = [];
  }
  try {
    const response = query.value.trim()
      ? await ConversationApi.search({
          q: query.value.trim(),
          page: page.value,
          searchBy: 'forward_recipient',
        })
      : await ConversationApi.get({
          status: 'all',
          assigneeType: 'all',
          page: page.value,
        });
    if (token !== generation) return;
    const payload =
      response.data?.data?.payload ?? response.data?.payload ?? [];
    const incoming = payload.filter(
      conversation => conversation.id !== props.sourceId
    );
    conversations.value = [
      ...new Map(
        [...conversations.value, ...incoming].map(conversation => [
          conversation.id,
          conversation,
        ])
      ).values(),
    ];
    hasMore.value = payload.length > 0;
  } catch {
    if (token === generation)
      error.value = t('CONVERSATION.FORWARD.LOAD_ERROR');
  } finally {
    if (token === generation) loading.value = false;
  }
}

function toggle(conversation) {
  if (busy.value || attempted.value || !selectable(conversation)) return;
  selected.value = [conversation];
}

function loadMore() {
  page.value += 1;
  fetchConversations(true);
}

async function forward() {
  if (!canSend.value) return;
  attempted.value = true;
  busy.value = true;
  error.value = '';
  try {
    const response = await ForwardingApi.forward({
      sourceId: props.sourceId,
      messageIds: props.messageIds,
      destinationIds: selected.value.map(item => item.id),
      requestId,
    });
    if (!Array.isArray(response.data?.results))
      throw new Error('Invalid forwarding response');
    results.value = response.data.results;
    if (active && pendingCount.value)
      statusTimer = setTimeout(refreshStatus, 3000);
  } catch (failure) {
    // A timeout may occur after the server commits. Never offer a new blind send.
    error.value = [401, 403, 422, 429].includes(failure.response?.status)
      ? t('CONVERSATION.FORWARD.REJECTED')
      : t('CONVERSATION.FORWARD.UNCERTAIN');
  } finally {
    busy.value = false;
  }
}

function back() {
  if (busy.value) return;
  if (attempted.value) emit('close');
  else emit('back');
}

function resultLabel(status) {
  const labels = {
    created: t('CONVERSATION.FORWARD.RESULT.CREATED'),
    scheduled: t('CONVERSATION.FORWARD.RESULT.SCHEDULED'),
    access_revoked: t('CONVERSATION.FORWARD.RESULT.ACCESS_REVOKED'),
    invalid_messages: t('CONVERSATION.FORWARD.RESULT.INVALID_MESSAGES'),
    invalid_destinations: t('CONVERSATION.FORWARD.RESULT.INVALID_DESTINATIONS'),
    unsupported_channel: t('CONVERSATION.FORWARD.RESULT.UNSUPPORTED_CHANNEL'),
    reply_window_closed: t('CONVERSATION.FORWARD.RESULT.REPLY_WINDOW_CLOSED'),
    attachment_too_large: t('CONVERSATION.FORWARD.RESULT.ATTACHMENT_TOO_LARGE'),
    unsupported_attachment: t(
      'CONVERSATION.FORWARD.RESULT.UNSUPPORTED_ATTACHMENT'
    ),
    creation_failed: t('CONVERSATION.FORWARD.RESULT.CREATION_FAILED'),
  };
  return labels[status] || t('CONVERSATION.FORWARD.UNCERTAIN');
}
const closeLabel = computed(() =>
  attempted.value
    ? t('CONVERSATION.FORWARD.CLOSE')
    : t('CONVERSATION.FORWARD.CANCEL')
);
function resultText(result) {
  const conversation = selected.value.find(
    item => item.id === result.destination_id
  );
  return t('CONVERSATION.FORWARD.RESULT_LINE', {
    name: recipient(conversation || {}).name || `#${result.destination_id}`,
    destination: result.destination_id,
    message: result.source_message_id,
    status: resultLabel(result.status),
  });
}

watch(query, () => {
  clearTimeout(timer);
  generation += 1;
  hasMore.value = false;
  timer = setTimeout(() => fetchConversations(), 300);
});

onMounted(() => {
  window.visualViewport?.addEventListener('resize', updateViewport);
  window.visualViewport?.addEventListener('scroll', updateViewport);
  window.addEventListener('resize', updateViewport);
  previousFocus = document.activeElement;
  dialog.value.showModal();
  searchInput.value.focus();
  fetchConversations();
});
onBeforeUnmount(() => {
  window.visualViewport?.removeEventListener('resize', updateViewport);
  window.visualViewport?.removeEventListener('scroll', updateViewport);
  window.removeEventListener('resize', updateViewport);
  clearTimeout(timer);
  clearTimeout(statusTimer);
  active = false;
  generation += 1;
  dialog.value?.close();
  previousFocus?.focus();
});
</script>

<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      :style="{
        '--forward-viewport-height': `${viewportHeight}px`,
        '--forward-offset-top': `${viewportTop}px`,
      }"
      class="forward-dialog bg-n-surface-1 text-n-slate-12 border border-n-weak rounded-lg p-0"
      aria-labelledby="forward-title"
      :aria-busy="busy"
      @cancel.prevent="back"
    >
      <div class="forward-dialog-inner flex flex-col min-h-0">
        <header class="flex items-center gap-3 p-3 border-b border-n-weak">
          <Button
            icon="i-ph-arrow-left"
            :aria-label="$t('CONVERSATION.FORWARD.BACK')"
            :disabled="busy"
            slate
            ghost
            class="min-h-11 min-w-11"
            @click="back"
          />
          <h2 id="forward-title" class="text-base font-semibold m-0">
            {{ $t('CONVERSATION.FORWARD.TITLE') }}
          </h2>
        </header>
        <div v-if="!attempted" class="p-3">
          <input
            ref="searchInput"
            v-model="query"
            type="search"
            :aria-label="$t('CONVERSATION.FORWARD.SEARCH')"
            :placeholder="$t('CONVERSATION.FORWARD.SEARCH')"
            class="w-full min-h-11 m-0 rounded border border-n-weak bg-n-surface-2 text-n-slate-12"
          />
        </div>
        <div class="forward-recipient-list min-h-0 flex-1 overflow-y-auto p-3">
          <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
            {{ error }}
          </p>
          <Button
            v-if="statusFailed"
            icon="i-ph-arrow-clockwise"
            :aria-label="$t('CONVERSATION.FORWARD.REFRESH_STATUS')"
            :title="$t('CONVERSATION.FORWARD.REFRESH_STATUS')"
            slate
            ghost
            class="min-h-11 min-w-11"
            @click="refreshStatus"
          />
          <template v-if="!attempted">
            <label
              v-for="conversation in conversations"
              :key="conversation.id"
              class="flex items-center gap-3 min-h-16 py-2 border-b border-n-weak"
              :class="{ 'opacity-50': !selectable(conversation) }"
            >
              <input
                type="radio"
                name="forward-destination"
                class="forward-checkbox shrink-0"
                :checked="selected.some(item => item.id === conversation.id)"
                :disabled="!selectable(conversation)"
                @change="toggle(conversation)"
              />
              <Avatar
                :name="recipient(conversation).name || `#${conversation.id}`"
                :src="recipient(conversation).thumbnail || ''"
                :size="32"
              />
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium break-words">{{
                  recipient(conversation).name || `#${conversation.id}`
                }}</span>
                <span class="block text-xs text-n-slate-11 break-words">
                  {{
                    $t('CONVERSATION.FORWARD.DESTINATION', {
                      channel: inboxFor(conversation).name,
                      id: conversation.id,
                    })
                  }}
                </span>
                <span v-if="!selectable(conversation)" class="block text-xs">{{
                  $t('CONVERSATION.FORWARD.UNAVAILABLE')
                }}</span>
                <span
                  v-if="conversation.labels?.length"
                  class="block text-xs text-n-slate-11 break-words"
                >
                  {{ conversation.labels.join(', ') }}
                </span>
              </span>
            </label>
            <p
              v-if="!loading && !conversations.length && !error"
              class="text-sm"
            >
              {{ $t('CONVERSATION.FORWARD.EMPTY') }}
            </p>
            <Button
              v-if="hasMore && !loading"
              :label="$t('CONVERSATION.FORWARD.MORE')"
              slate
              ghost
              class="min-h-11 mt-2"
              @click="loadMore"
            />
            <p v-if="loading" role="status" class="text-sm">
              {{ $t('CONVERSATION.FORWARD.LOADING') }}
            </p>
          </template>
          <p v-if="busy" role="status">
            {{ $t('CONVERSATION.FORWARD.SENDING') }}
          </p>
          <ul
            v-if="results.length"
            class="m-0 p-0 list-none"
            aria-live="polite"
          >
            <li
              v-for="result in results"
              :key="`${result.destination_id}-${result.source_message_id}`"
              class="py-2 border-b border-n-weak text-sm break-words"
            >
              {{ resultText(result) }}
            </li>
          </ul>
        </div>
        <footer class="forward-dialog-footer border-t border-n-weak p-3">
          <p v-if="selected.length" class="text-sm mb-2 mt-0 break-words">
            {{
              $t('CONVERSATION.FORWARD.DESTINATION', {
                channel: recipient(selected[0]).name,
                id: selected[0].id,
              })
            }}
          </p>
          <p v-if="pendingCount" role="status" class="text-sm mb-2 mt-0">
            {{
              $t('CONVERSATION.FORWARD.BATCH_PENDING', {
                count: pendingCount,
              })
            }}
          </p>
          <p
            v-if="!attempted && messageIds.length > 20"
            class="text-sm mb-2 mt-0"
          >
            {{
              $t('CONVERSATION.FORWARD.BATCH_SPLIT', {
                first: 20,
                second: messageIds.length - 20,
              })
            }}
          </p>
          <p class="text-sm mb-2 mt-0" role="status">
            {{
              $t('CONVERSATION.FORWARD.TOTAL', {
                messages: messageIds.length,
                recipients: selected.length,
                total,
              })
            }}
          </p>
          <div class="flex justify-between gap-2">
            <Button
              :label="closeLabel"
              :disabled="busy"
              slate
              ghost
              class="min-h-11"
              @click="$emit('close')"
            />
            <Button
              v-if="!attempted || busy"
              :label="$t('CONVERSATION.FORWARD.SEND')"
              icon="i-ph-arrow-bend-up-right"
              :disabled="!canSend"
              :is-loading="busy"
              class="min-h-11"
              @click="forward"
            />
          </div>
        </footer>
      </div>
    </dialog>
  </Teleport>
</template>

<style scoped>
.forward-dialog {
  width: min(560px, calc(100vw - 32px));
  max-width: none;
  max-height: calc(var(--forward-viewport-height, 100dvh) - 32px);
}
.forward-dialog::backdrop {
  background: rgb(0 0 0 / 50%);
}
.forward-dialog-inner {
  height: min(640px, calc(var(--forward-viewport-height, 100dvh) - 34px));
}
.forward-checkbox {
  width: 20px;
  height: 20px;
  margin: 12px;
  accent-color: var(--color-primary);
}
.forward-dialog-footer {
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}
@media (max-width: 640px) {
  .forward-dialog {
    width: 100vw;
    height: var(--forward-viewport-height, 100dvh);
    position: fixed;
    top: var(--forward-offset-top, 0px);
    max-height: none;
    margin: 0;
    border: 0;
    border-radius: 0;
  }
  .forward-dialog-inner {
    height: 100%;
  }
}
</style>
